// ManualAddItemGrid.jsx
import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import { v4 as uuidv4 } from 'uuid';

// FIX 1: Added the missing "weight" column to the grid
const columns = [
  { field: "nsn", headerName: "NSN", minWidth: 150, flex: 1, editable: true, align: "center", headerAlign: "center" },
  { field: "niin", headerName: "NIIN", minWidth: 150, flex: 1, editable: true, align: "center", headerAlign: "center" },
  { field: "name", headerName: "Part Number", minWidth: 150, flex: 1.2, editable: true, align: "center", headerAlign: "center" },
  { field: "nomenclature", headerName: "Nomenclature", minWidth: 200, flex: 1.5, editable: true, align: "center", headerAlign: "center" },
  { field: "weight", headerName: "Unit Weight", type: "number", minWidth: 120, flex: 1, editable: true, align: "center", headerAlign: "center" },
  { field: "length", headerName: "Unit Length", type: "number", minWidth: 120, flex: 1, editable: true, align: "center", headerAlign: "center" },
  { field: "width", headerName: "Unit Width", type: "number", minWidth: 120, flex: 1, editable: true, align: "center", headerAlign: "center" },
  { field: "height", headerName: "Unit Depth", type: "number", minWidth: 120, flex: 1, editable: true, align: "center", headerAlign: "center" },
  { field: "authorizedqty", headerName: "Authorized Qty", type: "number", minWidth: 130, flex: 1, editable: true, align: "center", headerAlign: "center" },
  { field: "qty", headerName: "On Hand Qty", type: "number", minWidth: 120, flex: 1, editable: true, align: "center", headerAlign: "center" },
  { field: "storagetype", headerName: "Storage Type", minWidth: 120, flex: 1, editable: true, align: "center", headerAlign: "center" },
  { field: "ksn", headerName: "Kit Serial Number (KSN)", minWidth: 150, flex: 1, editable: true, align: "center", headerAlign: "center" },
  { field: "shopcode", headerName: "Shop Code", minWidth: 120, flex: 1, editable: true, align: "center", headerAlign: "center" },
  { field: "location", headerName: "Location", minWidth: 150, flex: 1, editable: true, align: "center", headerAlign: "center" },
  { field: "loadorder", headerName: "Load Order", type: "number", minWidth: 120, flex: 1, editable: true, align: "center", headerAlign: "center" },
  { field: "maxsupportedweight", headerName: "Max Supported Wt.", type: "number", minWidth: 150, flex: 1, editable: true, align: "center", headerAlign: "center" },

];

// FIX 2: Added all required fields with default values to match the backend API
const emptyRow = {
    nsn: '',
    niin: '',
    name: '',
    nomenclature: '',
    weight: 0,
    length: 0,
    width: 0,
    height: 0,
    authorizedqty: 0,
    qty: 0,
    storagetype: '',
    ksn: '',
    shopcode: '',
    location: '',
    loadorder: 1,
    maxsupportedweight: 0, // Corrected typo here
};

export const ManualAddItemGrid = ({ onSave, onCancel }) => {
    // ... (the rest of the component logic is unchanged)
    const [manualRows, setManualRows] = useState([{ id: uuidv4(), ...emptyRow }]);
    const [isSaving, setIsSaving] = useState(false);

    const processRowUpdate = (newRow) => {
        setManualRows((prevRows) => prevRows.map((row) => (row.id === newRow.id ? newRow : row)));
        return newRow;
    };

    const handleAddRow = () => {
        setManualRows((prevRows) => [...prevRows, { id: uuidv4(), ...emptyRow }]);
    };

    const handleClear = () => {
        setManualRows([{ id: uuidv4(), ...emptyRow }]);
    };

    const handleSave = async () => {
        const validRows = manualRows.filter(row => row.niin && String(row.niin).trim() !== '');
        if (validRows.length === 0) {
            alert("Please provide a NIIN for each new item.");
            return;
        }

        setIsSaving(true);
        try {
            await onSave(validRows);
        } catch (error) {
            console.error("Failed to save items:", error);
            alert("An error occurred while saving. Please check the console for details.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <Button startIcon={<AddIcon />} onClick={handleAddRow} sx={{ mb: 2, flexShrink: 0 }}>
                Add Row
            </Button>

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <DataGrid
                    rows={manualRows}
                    columns={columns}
                    processRowUpdate={processRowUpdate}
                />
            </Box>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, flexShrink: 0 }}>
                <Button onClick={onCancel} disabled={isSaving}>Cancel</Button>
                <Button onClick={handleClear} disabled={isSaving}>Clear</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSaving ? 'Saving...' : 'Save and Add'}
                </Button>
            </Box>
        </Box>
    );
};

export default ManualAddItemGrid;